// Shared UI strings used across many components: verbs, generic labels, field
// names, states and the month abbreviations. Area-specific dictionaries
// (nav/auth/dashboard/database/analyser/inbox/poTracking/settings) must NOT
// redefine these keys.
//
// Key = the exact English source string used in the code. Value = German.
//
// Terminology is kept in step with FLIXPECTOR, which runs the same mechanism in
// the other direction: Bearbeiter = Handler, Kanzlei = law firm,
// Rechnungsdatum = invoice date, In Prüfung = In review, Abmelden = Sign out.

export const common: Record<string, string> = {
  // Generic actions
  Save: 'Speichern',
  Cancel: 'Abbrechen',
  Close: 'Schließen',
  Delete: 'Löschen',
  Edit: 'Bearbeiten',
  Add: 'Hinzufügen',
  Create: 'Anlegen',
  Update: 'Aktualisieren',
  Apply: 'Übernehmen',
  Refresh: 'Aktualisieren',
  Import: 'Importieren',
  Export: 'Exportieren',
  Download: 'Herunterladen',
  Entity: 'Gesellschaft',
  'Legal counsel': 'Legal Counsel',
  'In a new tab': 'In neuem Tab',
  Discard: 'Verwerfen',
  'Try again': 'Nochmal versuchen',
  Reload: 'Neu laden',
  Set: 'Setzen',
  Yes: 'Ja',
  No: 'Nein',
  Collapse: 'Einklappen',
  Expand: 'Aufklappen',
  'show / hide': 'ein-/ausklappen',
  'Show details': 'Details anzeigen',

  // Generic states
  'Loading …': 'Lädt …',
  'Saving …': 'Speichern …',
  'No data.': 'Keine Daten.',
  Unassigned: 'Nicht zugeordnet',
  Unknown: 'Unbekannt',
  'Unknown error': 'Unbekannter Fehler',
  'Something went wrong': 'Ein Fehler ist aufgetreten',
  'Please reload the page. If the problem persists, check your Excel file for invalid data or formats.':
    'Bitte lade die Seite neu. Wenn das Problem bestehen bleibt, prüfe deine Excel-Datei auf ungültige Daten oder Formate.',
  'Action failed': 'Aktion fehlgeschlagen',
  'e.g.': 'z. B.',
  'Warning:': 'Achtung:',
  'Selected:': 'Ausgewählt:',
  data: 'Daten',

  // Confidence levels, as the API delivers them (high / medium / low)
  high: 'hoch',
  medium: 'mittel',
  low: 'niedrig',
  Confidence: 'Erkennungsgüte',

  // Field names shared by the analyser, the inbox drawer and the database grid
  Supplier: 'Lieferant',
  Recipient: 'Empfänger',
  Invoice: 'Rechnung',
  Invoices: 'Rechnungen',
  invoices: 'Rechnungen',
  Suppliers: 'Lieferanten',
  suppliers: 'Lieferanten',
  'Invoice number': 'Rechnungsnummer',
  'Invoice date': 'Rechnungsdatum',
  'Gross amount': 'Betrag brutto',
  Currency: 'Währung',
  Net: 'Netto',
  Gross: 'Brutto',
  VAT: 'Umsatzsteuer',
  'VAT ID': 'USt-ID',
  'Tax-free': 'Steuerfrei',
  'Taxable net': 'Steuerpflichtiges Netto',
  'Tax details': 'Steuerdetails',
  Project: 'Projekt',
  Comment: 'Kommentar',
  Status: 'Status',
  Type: 'Typ',
  Date: 'Datum',
  Year: 'Jahr',
  Description: 'Beschreibung',
  Origin: 'Herkunft',
  Original: 'Original',
  Source: 'Beleg',
  Document: 'Dokument',
  Actions: 'Aktionen',
  Handler: 'Bearbeiter',
  'Choose a handler first': 'Erst einen Bearbeiter wählen',
  ID: 'ID',

  // Invoice status (utils/invoiceStatus.ts) — "In review" matches Flixpector
  Open: 'Offen',
  'In review': 'In Prüfung',
  Approved: 'Freigegeben',
  Paid: 'Bezahlt',
  Rejected: 'Abgelehnt',
  'Not for us': 'Nicht für uns',
  Overdue: 'Überfällig',
  Invoiced: 'Abgerechnet',
  overdue: 'überfällig',

  // Document type (utils/documentType.ts)
  'Credit note': 'Gutschrift',
  Cancellation: 'Storno',
  'Credit notes and cancellations are usually negative — is the amount sign correct?':
    'Gutschriften und Stornos sind üblicherweise negativ — stimmt das Vorzeichen des Betrags?',
  'An invoice is usually positive — is the amount sign correct?':
    'Eine Rechnung ist üblicherweise positiv — stimmt das Vorzeichen des Betrags?',

  // Review notes (ui/Feedback.tsx)
  'Review notes': 'Prüfhinweise',
  'review note': 'Prüfhinweis',
  'review notes': 'Prüfhinweise',

  // Month abbreviations — chart axes. The API and utils/filterUtils.ts both
  // emit the English short forms, which are the keys here.
  Jan: 'Jan',
  Feb: 'Feb',
  Mar: 'Mär',
  Apr: 'Apr',
  May: 'Mai',
  Jun: 'Jun',
  Jul: 'Jul',
  Aug: 'Aug',
  Sep: 'Sep',
  Oct: 'Okt',
  Nov: 'Nov',
  Dec: 'Dez',
}
